import React, { useEffect, useMemo } from 'react';
import { MedicalDataTypes } from '../../../../../../primary-record-types';
import ResourceListCard from '../../../../../shared/ResourceSearch/ResourceListCard';
import { getIntl } from '../../../../../../utils/intl';
import useRelatedResourceSuggestions from '../../../../../../hooks/api/useRelatedResourceSuggestions';
import useMedicalProfileResource from '../../../../../../hooks/api/useMedicalProfileResource';
import { pluralResourceCategoryMessages, resourceCategoryMessages } from '../../../../../../utils/sharedMessages';
import LoadableResourceContainer, {
    placeholderResource,
} from '../../../../../shared/ResourceSearch/LoadableResourceContainer';
import SkeletonLoadingContainer from '../../../../../v2BaseComponents/SkeletonLoadingContainer';
import useResource from '../../../../../../hooks/api/useResource';
import Stack, { ContentStack } from '../../../../../v2BaseComponents/Stack';
import Text from '../../../../../v2BaseComponents/Text';
import { AnimateLoadedContent } from '../../../../../v2BaseComponents/Loader';
import { ActionButtonLayout } from '../../../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../../../../v2BaseComponents/MainContentContainer';
import Icon from '../../../../../v2BaseComponents/Icon';
import { PRScrollView } from '../../../../../shared/Scroll';

const intl = getIntl('suggested-list', {
    suggestionHeader:
        'Primary Record found {relatedCategoryText} to {resourceDisplay}.  Select and link those that apply.',
    pluralRelatedCategoryText: 'some {categoryText} that may relate',
    singularRelatedCategoryText: 'a {categoryText} that maybe relates',
    noSuggestions: 'No {categoryText} found to link.',
    generatingSuggestions: 'Generating suggestions...',
    link: 'Link',
    nameFallback: 'this data',
    manualText: 'Add/Link Manually',
    additionPretext: "If the suggestions aren't correct, would you like to see other suggested {categoryText}?",
    additionalSuggestions: 'Additional Suggestions',
    or: 'or',
});

export type SuggestedLinksOwnProps = {
    sourceResourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    relatedResourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    hasEditPermission: boolean;
    relatedResources: MedicalDataTypes.PrimaryRecordResource[];
    isLoadingRelatedResources: boolean;
    profileId: string;
    sourceResourceId: string;
    onManualLink: () => void;
    onShowRelatedList: () => void;
    onSuggestAdditions: () => void;
};

export type SuggestedLinksProps = SuggestedLinksOwnProps;

const SuggestedLinks: React.FC<SuggestedLinksProps> = ({
    relatedResourceCategory,
    relatedResources,
    profileId,
    sourceResourceId,
    sourceResourceCategory,
    onManualLink,
    onShowRelatedList,
    onSuggestAdditions,
}) => {
    const [selectedResourceIds, setSelectedResourceIds] = React.useState<string[]>([]);
    const resource = useMedicalProfileResource({
        medicalProfileId: profileId,
        resourceCategory: sourceResourceCategory,
        resourceId: sourceResourceId,
    });
    const suggestions = useRelatedResourceSuggestions({
        profileId,
        resourceId: sourceResourceId,
        category: sourceResourceCategory,
        relatedCategory: relatedResourceCategory,
    });
    const isLoading = suggestions.isLoading || resource.isLoading;
    const hasResults = !!suggestions.suggestions?.suggestions?.length;
    const hasMultipleResults = (suggestions.suggestions?.suggestions?.length || 0) > 1;
    useEffect(() => {
        if (isLoading) {
            setSelectedResourceIds(relatedResources.map((_) => _.id));
        }
    }, [isLoading, relatedResources]);
    const hasMadeSelectionUpdates = useMemo(() => {
        if (selectedResourceIds.length !== relatedResources.length) return true;

        return selectedResourceIds.some((id) => !relatedResources.find((_) => _.id === id));
    }, [selectedResourceIds, relatedResources]);
    const { updateRelatedResources, isUpdatingRelatedResources } = useResource({
        medicalProfileId: profileId,
        resourceId: sourceResourceId,
        resourceCategory: sourceResourceCategory,
        relatedResourceCategories: [],
        onUpdateRelatedResourcesSuccess: onShowRelatedList,
    });

    const getMessages = () => {
        if (isLoading) return null;

        const categoryText = hasMultipleResults
            ? pluralResourceCategoryMessages(relatedResourceCategory)
            : resourceCategoryMessages(relatedResourceCategory);
        const relatedCategoryText = intl(
            hasMultipleResults ? 'pluralRelatedCategoryText' : 'singularRelatedCategoryText',
            { categoryText },
        );
        const resourceDisplay = resource.resource?.name ? `"${resource.resource.name}"` : intl('nameFallback');
        return {
            header: intl('suggestionHeader', { relatedCategoryText, resourceDisplay }),
            noSuggestions: intl('noSuggestions', { categoryText }),
            categoryText,
        };
    };

    const getHeader = () => {
        if (isLoading) return null;

        const messages = getMessages();
        if (suggestions.suggestions?.suggestions.length === 0) {
            <Stack horizontal justify={'center'} fullWidth>
                <Text variant="body-1" alignText="center">
                    {messages?.noSuggestions}
                </Text>
            </Stack>;
        } else {
            return (
                <Stack horizontal align={'center'} justify={'flex-start'} space={1} fullWidth>
                    <Icon name="AISparkle" size="button-icon" color="Primary-Reg" />
                    <Text variant="body-1">{messages?.header}</Text>
                </Stack>
            );
        }
    };

    const renderResults = () => (
        <>
            {getHeader()}
            <PRScrollView style={{ flex: 1, width: '100%' }}>
                {suggestions.suggestions?.suggestions?.map((suggestion) => (
                    <LoadableResourceContainer
                        key={suggestion.resourceId}
                        resourceId={suggestion.resourceId}
                        medicalProfileId={profileId}
                        resourceType={relatedResourceCategory}
                        render={({ resource, isLoading }) => {
                            if (isLoading || !resource) {
                                <SkeletonLoadingContainer isLoading>
                                    <ResourceListCard
                                        resource={placeholderResource}
                                        category={placeholderResource.type}
                                        profileId={profileId}
                                        isSelectable={false}
                                    />
                                </SkeletonLoadingContainer>;
                            } else {
                                return (
                                    <ResourceListCard
                                        resource={resource}
                                        category={resource.type}
                                        profileId={profileId}
                                        isSelectable
                                        explanation={suggestion.explanation}
                                        isSelected={selectedResourceIds.includes(resource.id)}
                                        onSelect={() => {
                                            if (selectedResourceIds.includes(resource.id)) {
                                                setSelectedResourceIds((ids) => ids.filter((id) => id !== resource.id));
                                            } else {
                                                setSelectedResourceIds((ids) => [...ids, resource.id]);
                                            }
                                        }}
                                        isDisabled={isUpdatingRelatedResources}
                                    />
                                );
                            }
                        }}
                    />
                ))}
            </PRScrollView>
        </>
    );

    return (
        <ActionButtonLayout
            scrollable
            actions={[
                {
                    title: intl('link'),
                    icon: 'Link',
                    onPress: () => {
                        updateRelatedResources({
                            relatedResourceIds: selectedResourceIds,
                            relatedResourceCategory,
                        });
                    },
                    show: hasMadeSelectionUpdates && hasResults && !isLoading,
                    isDisabled: isUpdatingRelatedResources || isLoading,
                },
                {
                    title: intl('additionalSuggestions'),
                    icon: 'PlusCircle',
                    onPress: onSuggestAdditions,
                    show: hasResults || isLoading,
                    isDisabled: isUpdatingRelatedResources || isLoading,
                },
                {
                    title: intl('manualText'),
                    icon: 'PlusCircle',
                    onPress: onManualLink,
                    show: !hasMadeSelectionUpdates,
                    isDisabled: isUpdatingRelatedResources,
                    testID: 'manual-add-link-button',
                },
            ]}
        >
            <AnimateLoadedContent isLoading={isLoading}>
                {() => (
                    <MainContentContainer>
                        <ContentStack>{renderResults()}</ContentStack>
                    </MainContentContainer>
                )}
            </AnimateLoadedContent>
        </ActionButtonLayout>
    );
};

export default React.memo(SuggestedLinks);
